import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Phone,
  Mail,
  MapPin,
  Building2,
  Calendar,
  MessageSquare,
  History,
  UserCheck,
  Send,
  Clock,
  Package,
  User,
} from 'lucide-react';
import { 
  getLead, 
  updateLead, 
  updateLeadStatus, 
  deleteLead, 
  addLeadComment, 
  deleteLeadComment,
  convertLead,
  getPackages 
} from '../../lib/api';
import { formatDate, formatRelativeTime } from '../../lib/utils';

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  negotiation: 'bg-purple-100 text-purple-700',
  contracting: 'bg-indigo-100 text-indigo-700',
  on_hold: 'bg-slate-100 text-slate-700',
  closed_won: 'bg-green-100 text-green-700',
  closed_lost: 'bg-red-100 text-red-700',
  postponed: 'bg-orange-100 text-orange-700',
};

const SOURCE_LABELS = {
  facebook: 'Facebook',
  web: 'Website',
  google_ads: 'Google Ads',
  referral: 'Referral',
  cold_call: 'Cold Call',
  trade_show: 'Trade Show',
  csv_upload: 'CSV Upload',
  other: 'Other',
};

const NO_PACKAGE_VALUE = 'none';

export default function LeadDetailPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState(null);
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [packages, setPackages] = useState([]);
  
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [newComment, setNewComment] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [interestedPackage, setInterestedPackage] = useState(NO_PACKAGE_VALUE);
  const [convertNotes, setConvertNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const loadLead = async () => {
    try {
      setLoading(true);
      const [leadRes, packagesRes] = await Promise.all([
        getLead(leadId),
        getPackages().catch(() => ({ data: [] }))
      ]);
      setLead(leadRes.data.lead);
      setComments(leadRes.data.comments || []);
      setHistory(leadRes.data.history || []);
      setStatuses(leadRes.data.statuses || {});
      setEditForm(leadRes.data.lead);
      setPackages(packagesRes.data?.packages || []);
    } catch (error) {
      toast.error('Failed to load lead');
      navigate('/sales/leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLead();
  }, [leadId]);

  const handleSaveLead = async () => {
    setSaving(true);
    try {
      await updateLead(leadId, editForm);
      toast.success('Lead updated');
      setShowEditDialog(false);
      loadLead();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update lead');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async () => {
    if (!newStatus) return;
    if (newStatus === 'postponed' && !followUpDate) {
      toast.error('Follow-up date is required for postponed status');
      return;
    }
    setSaving(true);
    try {
      await updateLeadStatus(leadId, newStatus, statusNotes, followUpDate || null);
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
      setShowStatusDialog(false);
      setNewStatus('');
      setStatusNotes('');
      setFollowUpDate('');
      loadLead();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setSaving(true);
    try {
      await addLeadComment(leadId, newComment);
      toast.success('Comment added');
      setNewComment('');
      loadLead();
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await deleteLeadComment(leadId, commentId);
      toast.success('Comment deleted');
      loadLead();
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const handleConvert = async () => {
    setSaving(true);
    try {
      const result = await convertLead(leadId, {
        interested_package: interestedPackage === NO_PACKAGE_VALUE ? null : interestedPackage,
        notes: convertNotes || null
      });
      toast.success('Lead converted to business account!');
      setShowConvertDialog(false);
      loadLead();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert lead');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await deleteLead(leadId);
      toast.success('Lead deleted');
      navigate('/sales/leads');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete lead');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Card><CardContent className="p-6"><Skeleton className="h-48" /></CardContent></Card>
      </div>
    );
  }

  if (!lead) return null;

  const isConverted = lead.status === 'closed_won';
  const isLost = lead.status === 'closed_lost';

  return (
    <div className="space-y-6" data-testid="lead-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/sales/leads')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Leads
        </Button>
      </div>

      {/* Lead Info Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center">
                <Building2 className="w-8 h-8 text-indigo-600" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold text-slate-900">{lead.business_name}</h1>
                  <Badge className={STATUS_COLORS[lead.status]}>
                    {lead.status.replace('_', ' ')}
                  </Badge>
                </div>
                {lead.contact_name && (
                  <p className="text-lg text-slate-600">{lead.contact_name}</p>
                )}
                <div className="flex flex-wrap gap-4 mt-3">
                  <a href={`tel:${lead.phone}`} className="flex items-center gap-1 text-indigo-600 hover:underline">
                    <Phone className="w-4 h-4" />
                    {lead.phone}
                  </a>
                  {lead.email && (
                    <a href={`mailto:${lead.email}`} className="flex items-center gap-1 text-indigo-600 hover:underline">
                      <Mail className="w-4 h-4" />
                      {lead.email}
                    </a>
                  )}
                  {lead.location && (
                    <span className="flex items-center gap-1 text-slate-600">
                      <MapPin className="w-4 h-4" />
                      {lead.location}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
                  <span className="capitalize">{lead.business_type?.replace('_', ' ')}</span>
                  <span>{SOURCE_LABELS[lead.source] || lead.source}</span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Created {formatDate(lead.created_at)}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              {!isConverted && !isLost && (
                <Button onClick={() => setShowConvertDialog(true)} className="bg-green-600 hover:bg-green-700">
                  <UserCheck className="w-4 h-4 mr-2" />
                  Convert
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowStatusDialog(true)}>
                Update Status
              </Button>
              <Button variant="outline" onClick={() => setShowEditDialog(true)}>
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
              {!isConverted && (
                <Button variant="destructive" onClick={handleDelete}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              )}
            </div>
          </div>
          {lead.message && (
            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium text-slate-500 mb-1">Message</p>
              <p className="text-slate-700">{lead.message}</p>
            </div>
          )}
          {isConverted && lead.converted_account_id && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 font-medium">
                Converted to business account on {formatDate(lead.converted_at)}
              </p>
              {lead.interested_package && (
                <p className="text-sm text-green-700 mt-1">
                  Package interest: {lead.interested_package}
                </p>
              )}
            </div>
          )}
          {lead.follow_up_date && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              <p className="text-amber-800">
                Follow up scheduled for <strong>{formatDate(lead.follow_up_date)}</strong>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="comments">
        <TabsList>
          <TabsTrigger value="comments" className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Comments ({comments.length})
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            History ({history.length})
          </TabsTrigger>
        </TabsList>

        {/* Comments Tab */}
        <TabsContent value="comments" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Notes & Comments</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Add Comment */}
              <div className="flex gap-3 mb-6">
                <Textarea
                  placeholder="Add a note or comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  rows={2}
                  className="flex-1"
                  data-testid="comment-input"
                />
                <Button 
                  onClick={handleAddComment} 
                  disabled={!newComment.trim() || saving}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>

              {/* Comments List */}
              {comments.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No comments yet</p>
              ) : (
                <div className="space-y-4">
                  {comments.map((comment) => (
                    <div key={comment.comment_id} className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                            <User className="w-4 h-4 text-indigo-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{comment.created_by_name}</p>
                            <p className="text-xs text-slate-500">{formatRelativeTime(comment.created_at)}</p>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteComment(comment.comment_id)}
                          className="text-slate-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-slate-700 ml-10">{comment.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Activity History</CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No history recorded</p>
              ) : (
                <div className="space-y-4">
                  {history.map((entry, index) => (
                    <div key={entry.history_id} className="flex items-start gap-4">
                      <div className={`w-3 h-3 mt-1.5 rounded-full ${index === 0 ? 'bg-indigo-500' : 'bg-slate-300'}`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-900 capitalize">
                            {entry.action.replace('_', ' ')}
                          </span>
                          {entry.old_value && (
                            <>
                              <Badge variant="outline" className="text-xs">{entry.old_value}</Badge>
                              <span className="text-slate-400">→</span>
                            </>
                          )}
                          {entry.new_value && (
                            <Badge className={STATUS_COLORS[entry.new_value] || 'bg-slate-100'}>{entry.new_value}</Badge>
                          )}
                        </div>
                        {entry.notes && (
                          <p className="text-sm text-slate-600 mt-1">{entry.notes}</p>
                        )}
                        <p className="text-xs text-slate-400 mt-1">{formatRelativeTime(entry.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Lead</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Business Name *</Label>
              <Input 
                value={editForm.business_name || ''} 
                onChange={(e) => setEditForm({ ...editForm, business_name: e.target.value })} 
              />
            </div>
            <div className="grid gap-2">
              <Label>Contact Name</Label>
              <Input 
                value={editForm.contact_name || ''} 
                onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })} 
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Phone *</Label>
                <Input 
                  value={editForm.phone || ''} 
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} 
                />
              </div>
              <div className="grid gap-2">
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={editForm.email || ''} 
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} 
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Location</Label>
              <Input 
                value={editForm.location || ''} 
                onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} 
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveLead} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Lead Status</DialogTitle>
            <DialogDescription>Change the status of this lead</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Current Status</Label>
              <Badge className={`${STATUS_COLORS[lead.status]} w-fit`}>
                {lead.status.replace('_', ' ')}
              </Badge>
            </div>
            <div className="grid gap-2">
              <Label>New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="status-select">
                  <SelectValue placeholder="Select new status" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(statuses).map(([key, value]) => (
                    <SelectItem key={key} value={key} disabled={key === lead.status}>
                      {value.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {newStatus === 'postponed' && (
              <div className="grid gap-2">
                <Label>Follow-up Date *</Label>
                <Input 
                  type="date" 
                  value={followUpDate}
                  onChange={(e) => setFollowUpDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            )}
            <div className="grid gap-2">
              <Label>Notes</Label>
              <Textarea 
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                placeholder="Add notes about this status change..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleStatusChange} 
              disabled={!newStatus || saving}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {saving ? 'Updating...' : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert Lead to Business Account</DialogTitle>
            <DialogDescription>
              Create a new business account from this lead
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium text-slate-900">{lead.business_name}</h4>
              {lead.contact_name && <p className="text-sm text-slate-600">{lead.contact_name}</p>}
              <p className="text-sm text-slate-500">{lead.phone}</p>
            </div>
            <div className="grid gap-2">
              <Label>Interested Package</Label>
              <Select value={interestedPackage} onValueChange={setInterestedPackage}>
                <SelectTrigger>
                  <SelectValue placeholder="Select package (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_PACKAGE_VALUE}>No package selected</SelectItem>
                  <SelectItem value="monthly">Monthly Plan</SelectItem>
                  <SelectItem value="yearly">Yearly Plan</SelectItem>
                  {packages.map((pkg) => (
                    <SelectItem key={pkg.package_id} value={pkg.name}>
                      {pkg.name} - {pkg.minutes} minutes
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Notes</Label>
              <Textarea 
                value={convertNotes}
                onChange={(e) => setConvertNotes(e.target.value)}
                placeholder="Any notes about this conversion..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleConvert} 
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
            >
              {saving ? 'Converting...' : 'Convert to Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
